import { type FC, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import { type ExtendedFileWithPath, formatTypeLabel, useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useUpdateEntity } from '@/hooks/data/inspector/useUpdateEntity';
import { useSelection } from '@/hooks/data/selection/useSelection';
import { Label, LabelVariants } from '@/components/ui/label';
import { NodeType, type TheNode } from '@/types/tree';
import { Textarea } from '@/components/ui/textarea';
import { cn, getUploadCsvKey } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KeyboardKey } from '@/lib/keyboard';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

export const InformationAccordion: FC<
  PropsWithChildren & {
    asset: Asset;
    folder: any;
    album: any;
    disabled: boolean;
    form?: any;
    currentSelectedType?: NodeType;
    selectedItems?: any;
    isLoading?: boolean;
    isFetching?: boolean;
    readOnly?: boolean;
    selectedAssetIdsLength?: number;
    temporaryAssets: Array<ExtendedFileWithPath>;
    activeVersion: any;
    selectedNode: TheNode;
    isUpload?: boolean;
    // Written to on every isEditName/isEditDescription change so inspector.tsx's reseed effect
    // can gate on whether a field is being edited right now, without owning that state itself.
    editingFieldsRef?: { current: { name: boolean; description: boolean } };
  }
> = ({
  asset,
  disabled,
  form,
  currentSelectedType,
  selectedItems,
  isLoading,
  isFetching,
  readOnly = false,
  children,
  temporaryAssets,
  activeVersion,
  isUpload,
  editingFieldsRef,
}) => {
  const [isEditName, setIsEditName] = useState(false);
  const [isEditDescription, setIsEditDescription] = useState(isUpload ? true : false);

  // The entity the panel is describing. Deliberately NOT the tree's active node: with a child
  // folder/album selected in the gallery, the panel describes that child, so the writes below
  // have to target it too.
  const { describedNodeId } = useSelection();
  const { control, getValues, handleSubmit, formState } = form;
  const { getActiveUploadSession, setManualMetadata } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const { selectedAssets: uploadSelectedAssetsIds, manualMetadata, csvData } = session;

  const tCommon = useTranslations('common');
  const tInformation = useTranslations('inspector.information');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const initialValuesRef = useRef(form.getValues());

  useEffect(() => {
    if (isUpload && uploadSelectedAssetsIds?.length > 0) {
      const description =
        manualMetadata?.[uploadSelectedAssetsIds[0].id]?.description ??
        csvData?.[getUploadCsvKey(uploadSelectedAssetsIds[0])]?.description ??
        '';
      form.setValue('description', description);
      initialValuesRef.current = form.getValues();
    }
  }, [isUpload, uploadSelectedAssetsIds, manualMetadata, csvData, form]);

  // Set Name and Description editing to false without saving when selecting different assets/folders/albums
  useEffect(() => {
    if (!isUpload) {
      setIsEditDescription(false);
      setIsEditName(false);
    }
  }, [currentSelectedType, asset?.id, selectedItems, describedNodeId, isUpload]);

  useEffect(() => {
    if (editingFieldsRef) {
      editingFieldsRef.current.name = isEditName;
    }
  }, [editingFieldsRef, isEditName]);

  useEffect(() => {
    if (editingFieldsRef) {
      editingFieldsRef.current.description = isEditDescription;
    }
  }, [editingFieldsRef, isEditDescription]);

  const { updateEntity, isPending: isInformationPending } = useUpdateEntity();

  const onRenameSubmit = async (vals: { name: string }) => {
    // /shares/[shareId]/assets/[assetId] page shows query error if the nodeId is outside of the submit functions
    const nodeId = currentSelectedType === NodeType.Assets ? selectedItems[0].id : describedNodeId;

    await updateEntity(currentSelectedType!, nodeId, { name: vals.name }, { name: formState.defaultValues?.name })
      .then((saved) => {
        // The backend can normalize/trim the name, so Cancel and the dirty check have to compare
        // against what it actually stored, not what was submitted.
        form.resetField('name', { defaultValue: saved.name ?? vals.name });
        setIsEditName(false);
      })
      .catch(() => setIsEditName(true));
  };

  const onDescriptionSubmit = async (vals: { description: string }) => {
    if (isUpload) {
      setManualMetadata(
        uploadSessionId,
        {
          description: vals.description,
        },
        [
          {
            id: uploadSelectedAssetsIds[0].id,
            name: uploadSelectedAssetsIds[0].name,
            originalName: uploadSelectedAssetsIds[0].originalName,
          },
        ],
        true,
      );
      initialValuesRef.current = getValues();
    } else {
      // /shares/[shareId]/assets/[assetId] page shows query error if the nodeId is outside of the submit functions
      const nodeId = currentSelectedType === NodeType.Assets ? selectedItems[0].id : describedNodeId;
      await updateEntity(
        currentSelectedType!,
        nodeId,
        { description: vals.description },
        { description: formState.defaultValues?.description },
      )
        .then((saved) => {
          form.resetField('description', { defaultValue: saved.description ?? vals.description });
          setIsEditDescription(false);
        })
        .catch(() => setIsEditDescription(true));
    }
  };

  // Only gate *opening* an editor on the fetch. Once someone is typing, a background refetch
  // taking Save and Cancel away from them is worse than a stale value.
  const nameEditorBusy = !isEditName && Boolean(isLoading || isFetching);
  const descriptionEditorBusy = !isEditDescription && Boolean(isLoading || isFetching);

  const raw = asset ?? temporaryAssets[0];
  const temporaryType = temporaryAssets[0]?.inferredType ?? temporaryAssets[0]?.type;
  // `activeVersion.fileType` is the coarse asset category (image/video/…), not a real
  // format — it only belongs at the end of the fallback chain. Shares don't always get a
  // populated `mimeType` from the backend, so fall back to the file extension first.
  const formatSourceValue =
    activeVersion?.mimeType ?? activeVersion?.originalFileName ?? activeVersion?.fileType ?? temporaryType;
  const formatSourceFileName = activeVersion?.originalFileName ?? temporaryAssets[0]?.name;
  const formatLabel = formatTypeLabel(formatSourceValue, formatSourceFileName);

  return (
    <Form {...form}>
      <div className="flex flex-col">
        {!disabled && !isUpload && (
          <div className="flex flex-col space-y-3 p-3">
            <form
              id="renameForm"
              onSubmit={handleSubmit(onRenameSubmit)}
              className="flex items-center justify-between gap-x-1.5"
            >
              <FormField
                control={control}
                name="name"
                render={({ field }) => {
                  const { ref: formRef, ...fieldProps } = field;

                  return (
                    <FormItem className={cn({ 'w-full': isEditName })}>
                      <FormControl>
                        {isEditName ? (
                          <Input
                            {...fieldProps}
                            ref={(el) => {
                              formRef(el);

                              // Focus the element when editing
                              if (el) {
                                el.focus();
                              }
                            }}
                            // isLoading={isLoading || isFetching || isInformationPending}
                            disabled={!isEditName || readOnly || disabled}
                            placeholder={tCommon('name')}
                            aria-invalid={Boolean(formState.errors.name)}
                            className="bg-input-background focus-within:ring-offset-ui-panel"
                            onBlur={(e) => {
                              const clickedSaveButton =
                                (e.relatedTarget as HTMLElement)?.id === 'inspector-information-name-save';

                              if (!clickedSaveButton) {
                                form.reset();
                                setIsEditName(false);
                              }
                            }}
                            blurOnEnter={false}
                            onKeyDown={(e) => {
                              if (e.key === KeyboardKey.Escape) {
                                form.resetField('name');
                                setIsEditName(false);
                              }
                            }}
                          />
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={cn('line-clamp-2 text-sm font-semibold wrap-anywhere')}>
                                {field.value}
                              </div>
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent className="max-w-96 wrap-anywhere">{field?.value}</TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {!readOnly && (
                <div className="flex gap-x-2">
                  {isEditName && (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={disabled || isInformationPending}
                      onClick={() => {
                        if (!isUpload) {
                          form.reset();
                          setIsEditName(false);
                        } else {
                          form.reset(initialValuesRef.current);
                        }
                      }}
                      data-cy="inspector-information-name-cancel"
                      className="h-5! min-h-5! rounded-md p-1 hover:bg-transparent!"
                    >
                      <FormLabel variant={LabelVariants.INSPECTOR_SECTION} className="mb-0 cursor-pointer">
                        {tCommon('cancel')}
                      </FormLabel>
                    </Button>
                  )}
                  <Button
                    type={isEditName ? 'submit' : 'button'}
                    form="renameForm"
                    variant="ghost"
                    disabled={
                      nameEditorBusy || disabled || isInformationPending || (isEditName && !formState.dirtyFields.name)
                    }
                    onClick={() => {
                      // When editing, the click submits natively and onRenameSubmit exits edit
                      // mode. Toggling state here would flip `tooltip`, remounting the button
                      // mid-click and cancelling the pending form submission.
                      if (isEditName) {
                        return;
                      }

                      if (isEditDescription) {
                        form.resetField('description');
                        setIsEditDescription(false);
                      }

                      setIsEditName(true);
                    }}
                    className={cn(
                      'icon-5! [&>svg]:icon-3! min-h-4! p-0 hover:bg-transparent! rounded-md -mr-1',
                      isEditName ? 'min-w-9!' : 'min-w-3!',
                    )}
                    id="inspector-information-name-save"
                    data-cy="inspector-information-name-save"
                    size="icon"
                    tooltip={isEditName ? undefined : tInformation('editName')}
                  >
                    {isEditName ? (
                      <FormLabel
                        variant={LabelVariants.INSPECTOR_SECTION}
                        className="mb-0 cursor-pointer text-highlight-base"
                      >
                        {tCommon('save')}
                      </FormLabel>
                    ) : (
                      <Pencil
                        className="icon-3 stroke-icon-base duration-150 hover:stroke-text-base"
                        strokeWidth={2.5}
                      />
                    )}
                  </Button>
                </div>
              )}
            </form>

            {currentSelectedType === NodeType.Assets && (
              <>
                <div className="text-xs text-text-gray-alternate">
                  {tInformation('uploadedOn', {
                    date: raw?.createdAt ? format(parseISO(raw.createdAt), "MMMM d, yyyy 'at' h:mm aaa") : '-',
                  })}
                </div>
                <div key={temporaryAssets[0]?.name} className="flex flex-col divide-y divide-ui-void-gap">
                  <div className="flex w-full flex-col space-y-3">
                    <div className="flex justify-between text-lg leading-4 text-text-gray">
                      <div className="flex flex-col">
                        <Label variant={LabelVariants.INSPECTOR_SECTION}>{tInformation('formatLabel')}</Label>
                        {(activeVersion ?? temporaryType) ? (
                          <div
                            className={cn('line-clamp-1! uppercase', {
                              'break-all wrap-anywhere': formatLabel.length > 10,
                            })}
                          >
                            {formatLabel}
                          </div>
                        ) : (
                          '-'
                        )}
                      </div>
                      {(activeVersion ?? temporaryType) && (
                        <div className="flex flex-col items-end">
                          <Label variant={LabelVariants.INSPECTOR_SECTION}>{tInformation('dimensionsLabel')}</Label>
                          {activeVersion?.width && activeVersion?.height
                            ? `${activeVersion.width}x${activeVersion.height}`
                            : '-'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {children}

        {!disabled && (
          <form id="descForm" onSubmit={handleSubmit(onDescriptionSubmit)} className="space-y-2 p-3">
            <div className="flex items-center justify-between">
              <FormLabel variant={LabelVariants.INSPECTOR_SECTION} className="mb-0">
                {tCommon('description')}
              </FormLabel>
              {!readOnly && (
                <div className="flex gap-x-2">
                  {isEditDescription && (
                    <Button
                      id="description-revert-button"
                      type="button"
                      variant="ghost"
                      disabled={disabled || isInformationPending}
                      onClick={() => {
                        if (isUpload) {
                          form.reset(initialValuesRef.current);
                        } else {
                          form.reset();
                          setIsEditDescription(false);
                        }
                      }}
                      className="h-5! min-h-5! rounded-md p-1 hover:bg-transparent!"
                      data-cy="inspector-information-description-cancel"
                    >
                      <FormLabel variant={LabelVariants.INSPECTOR_SECTION} className="mb-0 cursor-pointer">
                        {tCommon('cancel')}
                      </FormLabel>
                    </Button>
                  )}

                  {!isUpload && (
                    <Button
                      type={isEditDescription ? 'submit' : 'button'}
                      variant="ghost"
                      disabled={
                        descriptionEditorBusy ||
                        disabled ||
                        isInformationPending ||
                        (isEditDescription && !formState.dirtyFields.description)
                      }
                      onClick={() => {
                        // When editing, the click submits natively and onDescriptionSubmit exits
                        // edit mode. Toggling state here would flip `tooltip`, remounting the
                        // button mid-click and cancelling the pending form submission.
                        if (isEditDescription) {
                          return;
                        }

                        if (isEditName) {
                          form.resetField('name');
                          setIsEditName(false);
                        }

                        setIsEditDescription(true);
                      }}
                      className={cn(
                        'icon-5! [&>svg]:icon-3! min-h-4! p-0 hover:bg-transparent! rounded-md -mr-1',
                        isEditDescription ? 'min-w-8!' : 'min-w-3!',
                      )}
                      size="icon"
                      id="inspector-information-description-save"
                      data-cy="inspector-information-description-save"
                      tooltip={isEditDescription ? undefined : tInformation('editDescription')}
                    >
                      {isEditDescription ? (
                        <FormLabel
                          variant={LabelVariants.INSPECTOR_SECTION}
                          className="mb-0 cursor-pointer text-highlight-base"
                        >
                          {tCommon('save')}
                        </FormLabel>
                      ) : (
                        <Pencil
                          className="icon-3 stroke-icon-base duration-150 hover:stroke-text-base"
                          strokeWidth={2.5}
                        />
                      )}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <FormField
              control={control}
              name="description"
              render={({ field }) => {
                const { ref: formRef, ...fieldProps } = field;

                return (
                  <FormItem className="flex flex-col">
                    <FormControl>
                      {isEditDescription ? (
                        <Textarea
                          placeholder={tCommon('description')}
                          rows={4}
                          {...fieldProps}
                          ref={(el) => {
                            textareaRef.current = el;
                            formRef(el);

                            // Focus the element when editing
                            if (el && !isUpload) {
                              el.focus();
                            }
                          }}
                          onBlur={(e) => {
                            const clickedRevertButton = e.relatedTarget?.id === 'description-revert-button';
                            const clickedSaveButton =
                              (e.relatedTarget as HTMLElement)?.id === 'inspector-information-description-save';

                            if (isUpload && !clickedRevertButton) {
                              handleSubmit(onDescriptionSubmit)();
                            } else if (!isUpload && !clickedSaveButton) {
                              form.reset();
                              setIsEditDescription(false);
                            }
                          }}
                          disabled={!isEditDescription || readOnly || disabled}
                          // isLoading={isLoading || isFetching || isInformationPending}
                          textareaClassName="bg-input-background focus-within:ring-offset-ui-panel"
                          className="w-full text-[13px]!"
                          onKeyDown={(e) => {
                            if (!isUpload && e.key === KeyboardKey.Escape) {
                              form.resetField('description');
                              setIsEditDescription(false);
                            }
                          }}
                        />
                      ) : (
                        field.value &&
                        field.value?.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className={cn('line-clamp-4 text-[13px] wrap-anywhere')}>{field.value}</div>
                            </TooltipTrigger>
                            <TooltipPortal>
                              <TooltipContent className="max-w-96 wrap-anywhere">{field.value}</TooltipContent>
                            </TooltipPortal>
                          </Tooltip>
                        )
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </form>
        )}
      </div>
    </Form>
  );
};
